import React, { Component } from "react";
import { Capacitor } from "@capacitor/core";
import { StatusBar, Style } from "@capacitor/status-bar";
import { createTheme, ThemeProvider, Theme } from "@mui/material/styles";
import { withStyles } from "tss-react/mui";
import { version } from "../package.json";
import localForage from "localforage";
import Slider from "@mui/material/Slider";
import Select from "@mui/material/Select";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import Snackbar from "@mui/material/Snackbar";
import Typography from "@mui/material/Typography";
import { PieChart, Pie, Cell } from "recharts";

import foods from "./foods";
import foodGroups from "./groups";
import kcal from "./kcal";
import appStoreBadge from "./img/app-store-badge.png";
import googlePlayBadge from "./img/google-play-badge.png";

const storageKey = "planetary";

interface AppState {
  values: { [key: number]: number };
  age: number;
  sex: "m" | "f";
  activity: number;
  dragging: boolean;
  dragStart: number;
  pieValues: { [key: number]: number };
  width: number;
  showCopySnackbar: boolean;
}

interface PieData {
  name: string;
  value: number;
  background: string;
}

const styles = (theme: Theme) => ({
  root: {
    paddingBottom: "env(safe-area-inset-bottom)",
  },
  slider: {
    padding: `${theme.spacing(1)} 0`,
  },
  page: {
    maxWidth: 800,
    margin: "0 auto",
  },
  intro: {
    backgroundColor: theme.palette.primary.main,
    color: "white",
    padding: theme.spacing(2),
    paddingTop: `calc(${theme.spacing(2)} + env(safe-area-inset-top))`,
    textAlign: "justify" as const,
    position: "relative" as const,
  },
  heading: {
    marginBottom: theme.spacing(2),
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  headingTitle: {
    display: "flex",
    flexWrap: "wrap" as const,
    paddingRight: theme.spacing(2),
    fontWeight: 700,
  },
  primary: {
    fontWeight: 600,
  },
  secondary: {
    color: theme.palette.secondary.main,
  },
  subtitle: {
    color: theme.palette.secondary.main,
    fontWeight: 300,
  },
  info: {
    fontStyle: "italic" as const,
    fontWeight: 300,
    textAlign: "justify" as const,
    maxWidth: 500,
    margin: "0 auto",
  },
  select: {
    margin: theme.spacing(1),
  },
  selects: {
    margin: `${theme.spacing(1)} 0`,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  footer: {
    margin: `${theme.spacing(2)} auto`,
    fontStyle: "italic" as const,
    maxWidth: 500,
    textAlign: "justify" as const,
    padding: `0 ${theme.spacing(2)}`,
  },
  bottomDivider: {
    marginTop: theme.spacing(3),
  },
  noWrap: {
    whiteSpace: "nowrap" as const,
  },
  resetBtnContainer: {
    padding: theme.spacing(2),
    boxSizing: "border-box" as const,
    width: "100%",
    textAlign: "center" as const,
  },
  resetBtn: {},
  logo: {
    flexGrow: 0,
    width: 40,
    height: 40,
    minWidth: 40,
    [theme.breakpoints.up("md")]: {},
  },
  legend: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    flexWrap: "wrap" as const,
    marginTop: theme.spacing(2),
  },
  legendItem: {
    display: "flex",
    alignItems: "center",
    margin: `${theme.spacing(1)} 0`,
    flexGrow: 1,
    flexBasis: "50%",
    [theme.breakpoints.up("md")]: {
      flexBasis: "25%",
    },
  },
  legendBox: {
    width: theme.spacing(2),
    height: theme.spacing(2),
    marginRight: theme.spacing(1),
    marginLeft: theme.spacing(2),
  },
  badgeSection: {
    textAlign: "center" as const,
  },
  badge: {
    maxHeight: 80,
    maxWidth: "50%",
    padding: 10,
    boxSizing: "border-box" as const,
  },
  moreBtnContainer: {
    textAlign: "center" as const,
    marginTop: theme.spacing(2),
  },
});

const COLORS = [
  "#0088FE",
  "#00C49F",
  "#FFBB28",
  "#FF8042",
  "#8884D8",
  "#82CA9D",
  "#FFC658",
  "#FF6B6B",
];

const renderCustomizedLabel = ({
  cx,
  cy,
  midAngle,
  innerRadius,
  outerRadius,
  percent,
}: any) => {
  const RADIAN = Math.PI / 180;
  const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);

  return (
    <text
      x={x}
      y={y}
      fill="white"
      textAnchor={x > cx ? "start" : "end"}
      dominantBaseline="central"
    >
      {`${(percent * 100).toFixed(0)}%`}
    </text>
  );
};

interface SimplePieChartProps {
  data: PieData[];
  width: number;
}

const SimplePieChart: React.FC<SimplePieChartProps> = ({ data, width }) => (
  <PieChart
    width={width}
    height={width}
    style={{ margin: "auto", paddingRight: 12 }}
  >
    <Pie
      data={data}
      dataKey="value"
      cx={width / 2}
      cy={width / 2}
      labelLine={false}
      outerRadius={width / 2 - 16}
      startAngle={90}
      endAngle={450}
    >
      {data.map((entry, index) => (
        <Cell key={data[index].name} fill={data[index].background || "#000"} />
      ))}
    </Pie>
  </PieChart>
);

let saveTimer: NodeJS.Timeout | false = false;

const canChange = (
  i: number,
  values: { [key: number]: number },
  moved: { [key: number]: boolean },
  diff = 0,
) =>
  !(
    foods[i].gramsMin === foods[i].gramsMax ||
    (diff < 0 && values[i] === foods[i].gramsMax) ||
    (diff > 0 && values[i] === foods[i].gramsMin) ||
    (moved[i] &&
      (values[i] === foods[i].gramsMax || values[i] === foods[i].gramsMin))
  );

const total = (
  f: any[],
  values: { [key: number]: number },
  moved: { [key: number]: boolean },
  exc: number,
  onlyDiff = false,
  diff = 0,
) => {
  const a = f
    .map((food, i) =>
      exc === i || (onlyDiff && !canChange(i, values, moved, diff))
        ? 0
        : (food.kcal / food.grams) * values[i],
    )
    .reduce((a, b) => a + b, 0);
  return a;
};

const odp = (v: number) => Math.round(v * 2) / 2;

const getIngredients = (
  i: number,
  values: { [key: number]: number },
  ratio = 1,
) => {
  const food = foods[i];
  if (!food.weights) return "";

  const value = values[i] * ratio;
  const weight = food.weights[0];
  const count = odp(value / weight.weight);

  if (count === 0) return "";
  if (count === 1) return ` (${weight.singular})`;
  return ` (${count} ${weight.plural})`;
};

const save = (d: any) => {
  if (saveTimer) clearTimeout(saveTimer);
  saveTimer = setTimeout(() => {
    localForage.setItem(storageKey, d);
  }, 1000);
};

// True when running in a browser, false inside the Capacitor iOS/Android apps.
const isBrowser = !Capacitor.isNativePlatform();

interface AppProps {
  classes: any;
}

class App extends Component<AppProps, AppState> {
  constructor(props: AppProps) {
    super(props);
    this.state = {
      values: {},
      age: 19,
      sex: "f",
      activity: 2,
      dragging: false,
      dragStart: 0,
      pieValues: {},
      width: 0,
      showCopySnackbar: false,
    };
  }

  componentDidMount() {
    // console.log("App componentDidMount called");
    if (!isBrowser) {
      StatusBar.setBackgroundColor({ color: "#f06a00" });
      StatusBar.setStyle({ style: Style.Dark });
    }
    this.load();
    this.measure();
    window.addEventListener("resize", this.handleResize);
  }

  componentWillUnmount() {
    window.removeEventListener("resize", this.handleResize);
  }

  shouldComponentUpdate(nextProps: AppProps, nextState: AppState) {
    // Always update to ensure sliders work properly
    return true;
  }

  getKcal() {
    return kcal[this.state.sex][this.state.activity][this.state.age] || 2000;
  }

  getTotal(v = false, m = false) {
    return total(foods, this.state.values, {}, -1, v, m ? 1 : -1);
  }

  setSave(v: Partial<AppState>) {
    this.setState(v as AppState, () => {
      save(this.state);
    });
  }

  saveRef = (ref: HTMLDivElement) => {
    if (ref) {
      this.setState({ width: ref.offsetWidth });
    }
  };

  measure = () => {
    this.setState({ width: window.innerWidth });
  };

  handleResize = () => {
    this.setState({ width: window.innerWidth });
  };

  private touchStartX = 0;
  private touchStartY = 0;
  private touchIsVertical = false;

  changeValue = (i: number, value: number) => {
    // console.log("changeValue called with:", i, value);
    const { values } = this.state;
    const moved: { [key: number]: boolean } = {};
    moved[i] = true;

    // Calculate the calorie difference for this specific change
    // Use the current state value, handling 0 correctly
    const currentValue = values.hasOwnProperty(i) ? values[i] : foods[i].grams;
    const calorieDiff =
      (foods[i].kcal / foods[i].grams) * (value - currentValue);
    const baseAim = this.getKcal();

    // console.log(
    //   "currentValue:",
    //   currentValue,
    //   "value:",
    //   value,
    //   "calorieDiff:",
    //   calorieDiff,
    //   "baseAim:",
    //   baseAim,
    // );

    // Only proceed if there's actually a change
    if (Math.abs(calorieDiff) < 0.01) {
      // console.log("No significant change, ignoring");
      return;
    }

    if (Math.abs(calorieDiff) > baseAim * 0.1) {
      // console.log("Change rejected - too large");
      return;
    }

    // Calculate how much to adjust other sliders
    const adjustment = -calorieDiff / baseAim;
    const newValues = { ...values, [i]: value };

    // console.log("adjustment:", adjustment);

    // Adjust other sliders proportionally
    foods.forEach((food, j) => {
      if (j !== i && canChange(j, values, moved, adjustment)) {
        const currentValue = values.hasOwnProperty(j) ? values[j] : food.grams;
        const newValue = Math.max(
          food.gramsMin,
          Math.min(food.gramsMax, currentValue + adjustment * food.grams),
        );
        newValues[j] = newValue;
        // console.log(`Adjusting slider ${j}: ${currentValue} -> ${newValue}`);
      }
    });

    // console.log("Final newValues:", newValues);
    this.setSave({ values: newValues });
  };

  load = async () => {
    try {
      const saved = await localForage.getItem(storageKey);
      if (saved) {
        this.setState(saved as AppState);
      } else {
        const defaultValues: { [key: number]: number } = {};
        foods.forEach((food, i) => {
          defaultValues[i] = food.grams;
        });
        this.setSave({ values: defaultValues, pieValues: defaultValues });
      }
    } catch (error) {
      console.error("Error loading saved data:", error);
      const defaultValues: { [key: number]: number } = {};
      foods.forEach((food, i) => {
        defaultValues[i] = food.grams;
      });
      this.setSave({ values: defaultValues, pieValues: defaultValues });
    }
  };

  generateAIPrompt = () => {
    const { values, age, sex, activity } = this.state;
    const baseAim = this.getKcal();

    // Calculate daily totals for each food group
    const dailyTotals = foodGroups.map((group) => {
      const totalGrams = group.foods.reduce((sum, i) => {
        const value = values.hasOwnProperty(i) ? values[i] : foods[i].grams;
        return sum + value;
      }, 0);
      return {
        group: group.title,
        grams: Math.round(totalGrams),
        percentage: ((totalGrams / baseAim) * 100).toFixed(1),
      };
    });

    // Get individual food items with their values
    const foodItems = foods
      .map((food, i) => {
        const value = values.hasOwnProperty(i) ? values[i] : food.grams;
        const calories = (food.kcal / food.grams) * value;
        return {
          name: food.title,
          grams: Math.round(value),
          calories: Math.round(calories),
          group: foodGroups.find((g) => g.foods.includes(i))?.title || "Other",
        };
      })
      .filter((item) => item.grams > 0); // Only include foods with significant amounts

    // Generate age/sex/activity description
    const ageRanges = {
      2: "2-3 years",
      4: "4-8 years",
      9: "9-13 years",
      14: "14-18 years",
      19: "19-30 years",
      31: "31-50 years",
      51: "51+ years",
    };

    const activityLevels = {
      1: "low",
      2: "moderate",
      3: "high",
    };

    const prompt = `Create a detailed 7-day meal plan for a ${
      ageRanges[age] || "19-30"
    } year old ${sex === "m" ? "male" : "female"} with ${
      activityLevels[activity] || "moderate"
    } physical activity level.

Daily calorie target: ${baseAim} calories

DIETARY PREFERENCES (based on Planetary Health Diet):
${dailyTotals
  .map(
    (total) =>
      `- ${total.group}: ${total.grams} ${
        total.grams === 1 ? "gram" : "grams"
      } (${total.percentage}% of daily calories)`,
  )
  .join("\n")}

SPECIFIC FOOD ALLOCATIONS:
${foodItems
  .map(
    (item) =>
      `- ${item.name}: ${item.grams} ${item.grams === 1 ? "gram" : "grams"} (${
        item.calories
      } calories)`,
  )
  .join("\n")}

REQUIREMENTS:
1. Create 7 days of meals (breakfast, lunch, dinner, and 2 snacks)
2. Each meal should include specific quantities in grams
3. Ensure the daily totals match the allocations above
4. Include variety and seasonal ingredients
5. Provide simple, practical recipes
6. Consider food group balance across the week
7. Include nutritional information for each meal
8. Make it family-friendly and budget-conscious

Please format the response as:
- Day 1: [Meal plan with specific quantities]
- Day 2: [Meal plan with specific quantities]
- etc.

Include a shopping list for the week and any preparation tips.`;

    return prompt;
  };

  copyPromptToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(this.generateAIPrompt());
      this.setState({ showCopySnackbar: true });
    } catch (error) {
      console.error("Failed to copy prompt:", error);
      this.setState({ showCopySnackbar: true });
    }
  };

  render() {
    const { classes } = this.props;
    const {
      values,
      age,
      sex,
      activity,
      dragging,
      dragStart,
      pieValues,
      width,
    } = this.state;
    const baseAim = this.getKcal();

    const pie: PieData[] = foodGroups.map((g) => ({
      name: g.title,
      value: g.foods.reduce((sum, i) => sum + (values[i] || 0), 0),
      background: g.background || "#000",
    }));

    const defaultValues: { [key: number]: number } = {};
    foods.forEach((food, i) => {
      defaultValues[i] = food.grams;
    });

    return (
      <div className={classes.root}>
        {!isBrowser && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              height: "max(env(safe-area-inset-top), 24px)",
              backgroundColor: "#f06a00",
              zIndex: 9999,
              pointerEvents: "none",
            }}
          />
        )}
        <div className={classes.intro}>
          <ThemeProvider theme={createTheme({ palette: { mode: "dark" } })}>
            <div className={classes.heading}>
              <Typography variant="h4" className={classes.headingTitle}>
                <span className={classes.noWrap}>Planetary</span>&nbsp;
                <span className={classes.noWrap}>Health Diet</span>
              </Typography>
              <div className={classes.logo}>
                <svg
                  viewBox="0 0 100 100"
                  xmlns="http://www.w3.org/2000/svg"
                  width="40"
                  height="40"
                >
                  <path
                    d="m 31.537351,49.507814 c -0.630669,1.036762 -0.298235,1.100009 0.39844,1.300156 0.694293,0.199346 -0.298234,1.399427 0.496264,1.500303 0.794498,0.100074 2.581526,0.59964 3.077786,0 0.497853,-0.59964 -0.7937,-0.365872 -1.687612,-1.03276 -0.89391,-0.66769 -1.836333,-2.504242 -2.284878,-1.767699 z m 48.066757,16.29678 c -1.071261,0.537999 -0.596471,1.397828 -1.589792,1.99667 -0.993324,0.603644 -0.595676,0.800588 -0.894708,2.200016 -0.298238,1.401032 0.101001,2.19922 0.795292,1.10081 0.695091,-1.10081 2.879761,-5.897936 1.689208,-5.297496 z M 61.32985,32.212694 c -0.609992,0.184138 -1.09353,1.401031 -0.795295,2.000671 0.298233,0.599638 -0.298235,-0.301818 -1.091941,0.199347 -0.795294,0.500367 -1.975509,1.899801 -0.887549,1.999072 1.086374,0.10087 0.887549,-0.800581 1.880079,-0.399501 0.992524,0.399501 2.086055,0.30023 2.086055,-0.699707 7.77e-4,-1.00074 -0.19803,-3.4001 -1.191349,-3.099882 z m -6.4554,-4.599379 c -0.598061,0.08566 -0.795297,1.598774 0.497057,1.798924 1.292351,0.201748 3.603475,-0.298621 2.74456,-1.099208 -0.857327,-0.798989 -1.851444,-0.899863 -3.241617,-0.699716 z m -8.441251,33.792046 c -2.384289,-1.200881 -3.37602,-0.60044 -3.476226,-1.69965 -0.09862,-1.100811 -0.694293,-2.298489 -2.3827,-2.799657 -1.688411,-0.50037 -3.178792,-1.470682 -5.066021,-1.684438 -1.886438,-0.214558 -2.283288,1.284944 -3.275816,0.485155 -0.994115,-0.801389 -1.78782,-1.600377 -2.186262,-2.599511 -0.396851,-0.999135 -1.588202,-0.399494 -1.489585,-0.999135 0.09942,-0.600442 1.688411,-2.30009 -0.09862,-1.699649 -1.788613,0.59884 -3.377611,0.399494 -3.278199,-1.199283 0.100207,-1.601176 2.284878,-2.200017 3.178787,-2.400964 0.893912,-0.198545 1.589,0.901463 2.680936,1.599574 1.093532,0.699715 0.09941,-0.998334 0.695086,-2.199216 0.596471,-1.197681 2.881352,-1.098407 3.17879,-2.798858 0.297439,-1.698848 -0.09941,-3.098276 1.688407,-3.297625 1.787823,-0.200148 3.673462,0.09847 3.872287,-0.800589 0.198823,-0.899058 -2.085261,0.299422 -2.184673,-0.800583 -0.09942,-1.09841 1.985848,-2.597118 2.582318,-1.49951 0.595676,1.100009 2.085262,1.200083 2.284086,0.800595 0.198821,-0.399495 0,-1.099204 -0.694292,-2.79966 -0.695881,-1.69965 -1.390969,-1.199284 -1.987439,-3.09908 -0.596472,-1.899797 -1.588997,0.09848 -2.581523,-0.900661 -0.993324,-0.999136 -2.184675,-3.097479 -2.385086,-1.299358 -0.198028,1.799727 -0.198028,2.800461 -1.091143,4.199089 -0.894703,1.401029 -1.291558,0.301817 -2.086852,-0.59964 -0.794496,-0.900665 -2.581521,-1.799722 -1.985052,-2.597911 0.595676,-0.80059 1.191351,-2.899732 2.085259,-2.29929 0.893114,0.59884 2.284084,0.59884 2.085263,-0.402697 -0.198827,-0.998334 0,-1.398629 0.397646,-1.897396 0.396851,-0.499567 1.787818,-1.40023 1.787818,0.398693 0,1.799724 -0.19882,2.299292 1.192147,2.50024 1.389377,0.198546 2.979168,0.299418 2.879756,-0.59884 -0.100205,-0.900662 0.396855,-0.702118 0,-2.200019 -0.397644,-1.500304 -1.58979,-1.600378 -2.879756,-2.29929 -1.291557,-0.699714 -1.987439,-1.800525 -3.07938,-1.401031 -1.092732,0.400297 -1.192144,0.799788 -1.29076,1.900598 -0.09941,1.100008 -0.695087,1.498704 -1.091938,0.999135 -0.397647,-0.498766 0.09942,-2.099945 -1.390969,-1.098406 -1.489585,0.998333 -2.085258,2.697982 -2.878963,1.4987 -0.02461,-0.03761 -0.04533,-0.07444 -0.06523,-0.112884 -4.50534,3.830818 -7.970434,8.856909 -9.91811,14.587525 -0.289484,1.214495 -0.415144,2.570691 -0.04697,3.620263 0.595674,1.697248 1.49038,5.39757 2.284084,5.498444 0.794496,0.09847 -0.595674,-3.99894 0.198823,-2.700386 0.795292,1.299355 0.795292,3.232777 2.185466,4.566557 1.38938,1.332983 3.276611,1.132834 4.7654,2.431389 1.491175,1.300154 2.98076,3.199151 4.371729,2.999805 0,0 -0.100206,0.998333 -0.795295,2.697984 -0.695086,1.699648 -0.7937,3.697919 0.09942,4.895599 0.894705,1.202485 1.787821,3.00221 2.582319,3.901269 0.794499,0.900663 1.390172,5.69699 1.191349,8.097154 -0.09463,1.152847 0.10816,2.995801 0.618738,4.734679 0.696678,0.319436 1.405282,0.621256 2.126617,0.895861 0.05558,-1.663625 0.789723,-3.844429 1.717038,-4.230311 1.197714,-0.499567 2.289653,-2.500239 3.184356,-3.898867 0.89391,-1.401031 3.177193,-1.999869 3.87388,-3.698719 0.695081,-1.698048 0.396851,-3.697919 0.793693,-4.598581 0.396857,-0.897459 1.489594,-2.898931 -0.894703,-4.096613 z m 2.822496,-43.506388 c -2.326232,0 -4.598386,0.248182 -6.789419,0.714126 -7.79e-4,0.0016 -0.0041,0.0032 -0.0049,0.004 -1.173849,1.020752 -2.979964,2.499436 -1.589792,2.499436 1.39097,0 2.06538,-0.731741 2.820113,0.232972 0.755525,0.966312 0.455704,2.466612 0.455704,3.166326 0,0.698914 -0.396852,2.299291 0.597266,4.297562 0.992529,2.00067 1.091145,3.300027 2.085256,2.00067 0.992535,-1.299354 3.376021,-3.799594 4.567372,-3.998941 1.192944,-0.200946 2.384295,-0.699714 2.979968,-1.70045 0.596472,-0.999134 0,-1.969447 0.992528,-2.685174 0.992524,-0.713322 0.793703,-1.613985 1.091935,-2.712391 0.101003,-0.373878 0.473997,-0.616457 0.878006,-0.802994 -2.585489,-0.661287 -5.293462,-1.015147 -8.084152,-1.015147 z m 26.871391,30.809052 c 0.595674,1.300156 1.787814,4.899603 2.978372,5.498443 0.790522,0.397091 2.014479,-0.180132 2.89646,-0.785378 0.05971,-0.812596 0.09942,-1.629996 0.09942,-2.457005 0,-0.437121 -0.01665,-0.870239 -0.03261,-1.303359 -0.0279,-0.0168 -0.05334,-0.03682 -0.08269,-0.05121 -1.008433,-0.547604 -0.744398,-1.028758 0.03574,-0.835816 -0.178941,-2.727607 -0.68316,-5.367147 -1.481634,-7.875391 -0.02299,0.288213 -0.04369,0.57322 -0.04369,0.813397 0,1.198484 0.297438,2.398566 0,2.699585 -0.298236,0.300223 -0.794499,-1.400228 -1.092732,-2.200016 -0.298236,-0.800588 -0.290286,-2.189613 0.547162,-2.961377 0.0032,-0.004 0.0066,-0.0056 0.01026,-0.0088 -1.363138,-3.60825 -3.338647,-6.914686 -5.796103,-9.792801 -0.48036,-0.04721 -0.813587,-0.269797 -0.719744,-0.642873 0.0119,-0.04962 0.02939,-0.09126 0.04779,-0.132095 -1.222364,-1.344989 -2.555276,-2.5827 -3.98124,-3.70993 -0.298234,0.129696 -0.627487,0.397094 -1.031495,0.851826 -1.688411,1.898197 -2.086057,3.498572 -3.078581,3.998143 -0.994116,0.499564 -1.789412,2.099942 -0.695882,2.499435 1.091939,0.399496 1.191351,0 2.086052,0.80059 0.894706,0.798187 2.086058,0.599636 2.284879,0.09927 0.198826,-0.500367 0.396056,-2.100745 0.495472,-2.700386 0.101001,-0.598841 1.110229,-2.597908 1.002066,-0.998333 -0.108156,1.597974 1.381428,1.597974 0.388106,2.698782 -0.992526,1.099209 -0.397648,2.000668 -2.382698,2.200017 -1.986645,0.200154 -2.087649,-0.999932 -3.177995,-0.400289 -1.093532,0.600443 -0.994116,0.999131 -1.391766,1.598781 -0.397646,0.599638 -2.482111,1.800517 -2.482111,1.800517 0,0 -1.688406,-0.599638 -0.893114,0.498772 0.793705,1.100804 1.489586,2.40016 0.793705,2.501034 -0.694292,0.09927 -2.581524,-1.00154 -2.581524,0.197741 0,1.200082 -0.298235,2.501039 0.497058,2.799661 0.562269,0.211354 0.92572,0.671693 1.43948,0.606046 -0.397645,0.175329 -0.761096,0.425113 -1.041833,0.794985 -0.772232,1.016746 -0.298235,2.797255 -1.885644,3.397698 -1.590586,0.601241 -1.590586,1.299354 -1.888821,2.599511 -0.296642,1.300155 0,4.598581 0.397645,4.898802 0.397649,0.298619 1.091939,2.797255 2.78194,2.597109 1.688407,-0.200149 3.474636,-0.277005 4.071107,0.0128 0.596471,0.287412 1.590589,-0.611649 1.887233,1.686839 0.298232,2.300092 0.994115,2.600315 1.390172,3.799594 0.39765,1.199283 -0.198822,3.699523 0,5.199024 0.198823,1.497901 1.588998,4.098214 1.588998,5.397568 0,1.300157 1.489584,1.898997 2.284878,1.39943 0.795295,-0.501169 2.481317,-1.500304 3.177994,-3.097478 0.694293,-1.599577 0.694293,-2.400165 1.787024,-4.500108 1.09274,-2.098346 0.795291,-3.597847 1.290762,-5.197423 0.497064,-1.599574 1.692387,-2.698784 3.328303,-4.500108 1.638309,-1.797322 1.342459,-2.797256 0,-2.697185 -1.340863,0.100876 -2.237161,-1.19928 -2.931449,-2.798855 -0.695883,-1.600379 -1.490377,-5.598519 -0.894701,-4.299164 z m -1.390174,-3.998138 c -0.09942,0.798987 0.298231,2.097539 -1.888033,1.598773 -2.183873,-0.499566 -2.183873,-1.700448 -3.076193,-0.799786 -0.894707,0.89906 -1.986645,1.698849 -3.278993,0.30022 -1.290762,-1.39943 -1.091939,-1.799722 -1.787823,-1.898997 -0.49388,-0.07203 -2.146499,-0.443527 -3.522357,-0.04002 0.106569,-0.06404 0.221094,-0.148911 0.344363,-0.260189 0.994115,-0.899864 0.795293,-1.199283 1.887232,-2.200821 1.093527,-0.99753 1.887233,-1.898195 2.682528,-0.99753 0.795293,0.899058 2.48211,1.098407 1.88723,2.09834 -0.596469,0.999137 -0.198825,1.598777 0.497057,0.999137 0.694292,-0.600441 1.686025,-0.999137 0.992529,-1.798923 -0.695883,-0.798987 -1.390174,-2.898933 -0.39765,-1.500305 0.994118,1.401031 1.290766,2.099146 1.886437,2.900533 0.595677,0.799787 1.192146,1.299355 1.390971,0.799787 0.19803,-0.501167 -0.09942,-1.601174 0.19803,-1.799721 0.297436,-0.200949 0,1.699647 0.894703,1.799721 0.891523,0.09927 1.38858,0 1.289969,0.79979 z m -1.290766,-2.900536 c -1.589791,0.499571 -0.318117,-1.111217 0.397647,-2.199214 0.638623,-0.971117 0.297441,0.30102 1.688411,0.399492 1.390176,0.101675 2.184673,1.700452 1.488788,1.300956 -0.696678,-0.39949 -1.986644,0 -3.574846,0.498766 z"
                    style={{
                      fill: "#ffffff",
                      stroke: "#ffffff",
                      strokeWidth: 0,
                    }}
                  />
                  <ellipse
                    style={{
                      fill: "none",
                      stroke: "#ffffff",
                      strokeWidth: 10.83152008,
                    }}
                    cx="50"
                    cy="50.000004"
                    rx="44.584244"
                    ry="44.58424"
                  />
                </svg>
              </div>
            </div>

            <div className={classes.selects}>
              <FormControl className={classes.select}>
                <InputLabel htmlFor="name-readonly">Age</InputLabel>
                <Select
                  value={age || 19}
                  onChange={(event) =>
                    this.setSave({ age: event.target.value as number })
                  }
                >
                  <MenuItem value={2}>2-3</MenuItem>
                  <MenuItem value={4}>4-8</MenuItem>
                  <MenuItem value={9}>9-13</MenuItem>
                  <MenuItem value={14}>14-18</MenuItem>
                  <MenuItem value={19}>19-30</MenuItem>
                  <MenuItem value={31}>31-50</MenuItem>
                  <MenuItem value={51}>51+</MenuItem>
                </Select>
              </FormControl>
              <FormControl className={classes.select}>
                <InputLabel htmlFor="name-readonly">Sex</InputLabel>
                <Select
                  value={sex || "f"}
                  onChange={(event) =>
                    this.setSave({ sex: event.target.value as "m" | "f" })
                  }
                >
                  <MenuItem value="m">Male</MenuItem>
                  <MenuItem value="f">Female</MenuItem>
                </Select>
              </FormControl>
              <FormControl className={classes.select}>
                <InputLabel htmlFor="name-readonly" className={classes.noWrap}>
                  Physical activity
                </InputLabel>
                <Select
                  value={activity || 2}
                  onChange={(event) =>
                    this.setSave({ activity: event.target.value as number })
                  }
                >
                  <MenuItem value={1}>Low</MenuItem>
                  <MenuItem value={2}>Moderate</MenuItem>
                  <MenuItem value={3}>High</MenuItem>
                </Select>
              </FormControl>
            </div>
            <Typography className={classes.info}>
              Move the sliders below to personalize your planetary health diet
              in line with the recommended quantities and ratios taken from the
              official report.
            </Typography>

            {isBrowser && (
              <Typography className={classes.moreBtnContainer}>
                <Button variant="outlined" component="a" href="about/">
                  What is the Planetary Health Diet?
                </Button>
              </Typography>
            )}
          </ThemeProvider>
        </div>
        <List className={classes.page}>
          {foods.map((food, i) => (
            <ListItem key={food.title}>
              <ListItemText
                primary={
                  food.subtitle ? (
                    <div>
                      {`${food.title} `}
                      <small
                        className={classes.subtitle}
                      >{`(${food.subtitle})`}</small>
                    </div>
                  ) : (
                    food.title
                  )
                }
                secondary={
                  <div>
                    {food.gramsMin !== food.gramsMax && (
                      <div
                        onTouchStart={(e) => {
                          this.touchStartX = e.touches[0].clientX;
                          this.touchStartY = e.touches[0].clientY;
                          this.touchIsVertical = false;
                        }}
                        onTouchMove={(e) => {
                          const dx = Math.abs(
                            e.touches[0].clientX - this.touchStartX,
                          );
                          const dy = Math.abs(
                            e.touches[0].clientY - this.touchStartY,
                          );
                          if (dy > dx && dy > 8) {
                            this.touchIsVertical = true;
                          }
                        }}
                      >
                        <ThemeProvider
                          theme={createTheme({
                            palette: {
                              primary: {
                                main:
                                  foodGroups.filter(
                                    (g) => g.foods.indexOf(i) >= 0,
                                  )[0]?.background || "#000",
                              },
                            },
                          })}
                        >
                          <Slider
                            value={
                              values.hasOwnProperty(i) ? values[i] : food.grams
                            }
                            min={food.gramsMin}
                            max={food.gramsMax}
                            step={
                              food.grams === Math.round(food.grams) ? 1 : 0.1
                            }
                            onChange={(event, value) => {
                              if (!this.touchIsVertical) {
                                this.changeValue(i, value as number);
                              }
                            }}
                            onDragStart={() => {
                              this.setSave({
                                dragging: true,
                                dragStart: new Date().getTime(),
                              });
                            }}
                            onChangeCommitted={() => {
                              this.touchIsVertical = false;
                              this.setSave({
                                dragging: false,
                                pieValues: values,
                              });
                            }}
                            className={classes.slider}
                          />
                        </ThemeProvider>
                      </div>
                    )}
                    <div>
                      {`${
                        Math.round((values[i] * this.getKcal()) / baseAim) || 0
                      }g `}
                      {values[i] > 0.1 &&
                        getIngredients(i, values, this.getKcal() / baseAim)}
                    </div>
                  </div>
                }
                secondaryTypographyProps={{ component: "div" }}
                classes={{
                  primary: classes.primary,
                  secondary: classes.secondary,
                }}
              />
            </ListItem>
          ))}
        </List>
        <div className={classes.resetBtnContainer}>
          <Button
            variant="contained"
            color="primary"
            className={classes.resetBtn}
            disabled={
              Object.values(values).join(",") ===
              Object.values(defaultValues).join(",")
            }
            onClick={() =>
              this.setSave({ values: defaultValues, pieValues: defaultValues })
            }
          >
            Reset all
          </Button>
        </div>
        <Divider />

        <div
          className={classes.page}
          style={{
            marginTop: 16,
            marginBottom: 16,
            maxWidth: "min(100% - 32px, 600px)",
            boxSizing: "border-box",
          }}
        >
          <Typography variant="h6" style={{ marginBottom: 16 }}>
            AI Meal Plan Generator
          </Typography>
          <Typography variant="body2" style={{ marginBottom: 16 }}>
            Use this prompt with ChatGPT (or similar) to generate a personalized
            weekly meal plan based on your current dietary preferences:
          </Typography>
          <div
            style={{
              backgroundColor: "#f5f5f5",
              padding: 16,
              borderRadius: 8,
              border: "1px solid #ddd",
              marginBottom: 16,
              fontFamily: "monospace",
              fontSize: "14px",
              whiteSpace: "pre-wrap",
              overflowX: "auto",
            }}
          >
            {this.generateAIPrompt()}
          </div>
          <Button
            variant="outlined"
            color="primary"
            onClick={() => this.copyPromptToClipboard()}
            style={{ marginRight: 8 }}
          >
            Copy Prompt
          </Button>
        </div>

        <Divider />
        <div className={classes.page}>
          <div className={classes.legend}>
            {foodGroups.map((g) => (
              <div className={classes.legendItem} key={g.title}>
                <span
                  style={{ backgroundColor: g.background || "#000" }}
                  className={classes.legendBox}
                />
                <Typography style={{ color: g.background || "#000" }}>
                  {g.title}
                </Typography>
              </div>
            ))}
          </div>
          <SimplePieChart data={pie} width={Math.min(width || 500, 500)} />
        </div>
        <div>
          <Divider className={classes.bottomDivider} />
          <Typography className={classes.footer}>
            Diet recommendations: EAT–Lancet Commission.
          </Typography>
          <Typography className={classes.footer}>
            Daily calorie requirements: Estimated Energy Requirements (EER) from
            the Institute of Medicine Dietary Reference Intakes macronutrients
            report, 2002.
          </Typography>
          <Typography className={classes.footer}>
            App by Mento Apps. Data from the EAT–Lancet Commission report.
          </Typography>
          <Typography className={classes.footer}>
            v{version} &copy; {new Date().getFullYear()} Mento Apps Ltd
          </Typography>
        </div>

        {isBrowser && (
          <div className={classes.badgeSection}>
            <a
              href="https://itunes.apple.com/us/app/planetary-health-diet/id1450024339?pt=119228314&mt=8&ct=badge"
              target="_blank"
              rel="noopener noreferrer"
            >
              <img
                src={appStoreBadge}
                alt="Download on the App Store"
                className={classes.badge}
              />
            </a>
            <a
              href="https://play.google.com/store/apps/details?id=uk.co.mento.planetary&referrer=utm_source%3Dwebsite%26utm_medium%3Dbadge"
              target="_blank"
              rel="noopener noreferrer"
            >
              <img
                src={googlePlayBadge}
                alt="Get it on Google Play"
                className={classes.badge}
              />
            </a>
          </div>
        )}

        <Snackbar
          open={this.state.showCopySnackbar}
          autoHideDuration={3000}
          onClose={() => this.setState({ showCopySnackbar: false })}
          message="Prompt copied to clipboard!"
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        />
      </div>
    );
  }
}

export default withStyles(App, styles);
